import { useCallback, useEffect, useRef, useState } from "react";
import internal from "stream";

interface TypeConfig { [key: string]: any }
/**
 * The configuration options.
 */
export interface DikstraConfig {
    routingData?: TypeConfig;
}
export interface Coordinate {
    latitude: number,  //緯度
    longitude: number  //経度
}

class RoadNode {
    edgesTo: RoadNode[] = [];          //この分岐点から進める経路の次の分岐点のID
    edgesCost: number[] = [];        //この分岐点から進める経路の次の分岐点までの距離 単位：メートル
    done = false;          //経路計算で訪問済みであるか
    cost = -1;             //経路検索でスタート位置から、この分岐点(node)までの合計距離 単位：メートル
    id = 0;                //経路の分岐点（角）識別ID
    previousNode: RoadNode | null = null;   //経路検索において、この分岐点(node)の前の分岐点(node)情報（object)

    constructor(id: number) {
      this.id = id;
    }

    /**
     * 現在の分岐点から次の分岐点までの情報を追加する
     * 
     * @param {*} node 次の分岐点のID
     * @param {*} cost 次の分岐点までの距離 単位：メートル
     */
    addNode(node: RoadNode, cost: number) {
        this.edgesTo.push(node);
        this.edgesCost.push(cost);      
    }
}

/**
 * ダイクストラ法
 *   グラフ上のある地点を始点とする最短経路を求める(単一始点最短経路問題を解く)ためのアルゴリズ
 * 
 *  [グラフ理論]
 *   交差点(頂点)のことを”ノード”、道(辺)のことを”エッジ”、各道の距離等を”重み(cost)”と呼ぶ
 * 
 * @see
 *   https://products.sint.co.jp/topsic/blog/dijkstras-algorithm
 */
export function useDikstra(config: DikstraConfig = {}) 
{
    let latlngs: { [key: number]: Coordinate; } = {};
    const{routingData} = config;

    for (const edge in routingData) {
        let nodes = routingData[edge];
        const [node_start, node_end] = edge.split('-');
        latlngs[Number(node_start)] = {
            latitude: Number(nodes[0].latitude),
            longitude: Number(nodes[0].longitude)
        };
        latlngs[Number(node_end)] = {
            latitude: Number(nodes[nodes.length - 1].latitude),
            longitude: Number(nodes[nodes.length - 1].longitude)
        };
    };

    const createNodes = () => {
        let nodes = [];
        for (const edge in latlngs) {
          nodes.push(new RoadNode(Number(edge)));
        }
        for (const edgeBetween in routingData) {
            const [node_start, node_end] = edgeBetween.split('-');
            let cost = 0;
            for (let i = 0; i < routingData[edgeBetween].length; i++) {
                if(i == 0){
                    continue;
                }
                //node_startからroutingDataまでの距離計算（複数分岐点があれば合算）
                cost += getDistance(
                    routingData[edgeBetween][i-1].latitude,
                    routingData[edgeBetween][i-1].longitude,
                    routingData[edgeBetween][i].latitude,
                    routingData[edgeBetween][i].longitude,
                );
                //座標FROM.addNode(座標TO, 距離m);
                nodes[Number(node_start)].addNode(nodes[Number(node_end)], cost);
            }
        }
        return nodes;
    };

    const getRoute = (p1:number, p2:number) =>
    {
        // console.log(p1, p2);
        return routingData![`${p1}-${p2}`];
    }

  /**
   * ダイクストラ法による経路検索
   * 
   * @param {*} start ルート検索の開始位置の位置(node)ID
   * @param {*} goal  ルート検索の目的位置の位置(node)ID
   * @returns 
   */
   const routing = (start:number, goal:number) => 
   {
        //console.log(start+":"+goal);


        //頂点(node)集合全体を取得
        let nodes = createNodes();

        // start node is first node
        nodes![start].cost = 0;
        while (true) {
            let processNode = null;
            for (let i = 0; i < nodes!.length; i++) {
              let node = nodes![i];
              // 訪問済み or まだコストが未設定
              if (node.done || node.cost < 0) {
                continue;
              }
              if (!processNode) {
                processNode = node;
                continue;
              }
              // 一番小さいコストのノードを探す
              if (node.cost < processNode.cost) {
                processNode = node;
              }
            }
            if (!processNode) {
              break;
            }
            
            //現時点で経路が判明している頂点として設定
            processNode.done = true;
      
            for (var i = 0; i < processNode.edgesTo.length; i++) {
              var node = processNode.edgesTo[i];
              var cost = processNode.cost + processNode.edgesCost[i];
              // コストが未設定 or コストの少ない経路がある場合はアップデート
              var needsUpdate = node.cost < 0 || node.cost > cost;
              if (needsUpdate) {
                node.cost = cost;
                node.previousNode = processNode;
              }
            }
        }// end whle  
          
        // console.log('Has been done to search path.');
        // console.log(nodes);
        let goalNode = nodes![goal];
        // console.log('最短経路コスト：' + goalNode.cost);
        let path = 'ゴール：' + goal;
        let currentNode = goalNode;
        let nextNode = null;
        let point = [goal];
        while (true) {
            nextNode = currentNode.previousNode;
            if (!nextNode) {
                path += '：スタート';
                break;
        }
        point.push(nextNode.id);
        path += ' ← ' + nextNode.id;
        currentNode = nextNode;
        }
        //console.log('最短経路');
        //console.log(path);
        // console.log(point);

        let ret:any = [];
        const pointer = point.reverse();
        // console.log(pointer);

        for (i = 0; i < pointer.length; i++) {
        if (point[i + 1] !== undefined) {
            let route = getRoute(point[i], point[i + 1]);
            ret = ret.concat(route);
        }
        }
        return ret;
   }

    /**
     * 2点間の緯度経度から距離（m）を返す
     * 
     * @param fromlat 
     * @param fromlng 
     * @param tolat 
     * @param tolng 
     * @returns 
     */
    const getDistance = (fromlat:number, fromlng:number, tolat:number, tolng:number) => 
    {
        if (!fromlat || !fromlng || !tolat || !tolng) {
            return 0;
        }
        let R = 6371; // Radius of the Earth in km
        let dLat = ((tolat - fromlat) * Math.PI) / 180;
        let dLon = ((tolng - fromlng) * Math.PI) / 180;
        let a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((fromlat * Math.PI) / 180) *
            Math.cos((tolat * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        let d = R * c * 1000; // km * 1000 = m

        return d;
    };    

    // 緯度経度から最も近いnodeIDを返す
    const getNearNode = (coordinate: Coordinate) => {
        let ret = null;
        let minDistance = -1;
        for (const node in latlngs) {
            let distance = getDistance(
                coordinate.latitude, 
                coordinate.longitude, 
                latlngs[node].latitude,
                latlngs[node].longitude
            );
            if(ret == null || (distance > 0 && distance < minDistance)){
                ret = node;
                minDistance = distance;
            }
        }
        return ret;
    };

    return {
        routing, getNearNode, getDistance
    }
}